/**
 * Equal-temperament pitch math.
 *
 * MIDI note 69 is the reference pitch A4; every other note is derived from it
 * by twelve-tone equal temperament. All functions are pure and take the
 * reference frequency explicitly so alternative tunings (432 Hz, 415 Hz, ...)
 * work without hidden global state.
 */

/** MIDI note number of the reference pitch A4. */
export const A4_MIDI = 69

/** Semitones per octave in twelve-tone equal temperament. */
export const SEMITONES_PER_OCTAVE = 12

/** Cents per semitone. */
export const CENTS_PER_SEMITONE = 100

/** Cents per octave. */
export const CENTS_PER_OCTAVE = SEMITONES_PER_OCTAVE * CENTS_PER_SEMITONE

/** Default concert pitch in Hz. */
export const DEFAULT_A4 = 440

/** A pitch resolved to the closest equal-tempered note. */
export interface NearestNote {
  /** Closest MIDI note number. */
  readonly midi: number
  /** Signed deviation from that note in cents, always within [-50, +50). */
  readonly cents: number
}

const log2 = (x: number): number => Math.log2(x)

function assertPositive(value: number, name: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a finite positive number, got ${String(value)}`)
  }
}

/**
 * Frequency of a MIDI note in the given tuning.
 *
 * @param midi - MIDI note number (may be fractional).
 * @param a4 - Reference frequency of A4 in Hz.
 * @returns Frequency in Hz.
 */
export function freqOf(midi: number, a4: number = DEFAULT_A4): number {
  if (!Number.isFinite(midi)) {
    throw new RangeError(`midi must be a finite number, got ${String(midi)}`)
  }
  assertPositive(a4, 'a4')
  return a4 * Math.pow(2, (midi - A4_MIDI) / SEMITONES_PER_OCTAVE)
}

/**
 * Signed interval between a measured frequency and a target frequency.
 *
 * @param f - Measured frequency in Hz.
 * @param fTarget - Target frequency in Hz.
 * @returns Deviation in cents; negative means flat, positive means sharp.
 */
export function centsOff(f: number, fTarget: number): number {
  assertPositive(f, 'f')
  assertPositive(fTarget, 'fTarget')
  return CENTS_PER_OCTAVE * log2(f / fTarget)
}

/**
 * Continuous MIDI note number of a frequency (inverse of {@link freqOf}).
 *
 * @param f - Frequency in Hz.
 * @param a4 - Reference frequency of A4 in Hz.
 * @returns Fractional MIDI note number.
 */
export function midiOf(f: number, a4: number = DEFAULT_A4): number {
  assertPositive(f, 'f')
  assertPositive(a4, 'a4')
  return A4_MIDI + SEMITONES_PER_OCTAVE * log2(f / a4)
}

/**
 * Snap a frequency to the closest equal-tempered note.
 *
 * A deviation of more than 50 cents snaps to the neighbouring note, so the
 * returned `cents` always stays within [-50, +50). Exact half-steps round up
 * (following {@link Math.round}), which yields -50 rather than +50 cents.
 *
 * @param f - Measured frequency in Hz.
 * @param a4 - Reference frequency of A4 in Hz.
 * @returns Closest MIDI note and the signed deviation in cents.
 */
export function nearestMidi(f: number, a4: number = DEFAULT_A4): NearestNote {
  const exact = midiOf(f, a4)
  const midi = Math.round(exact)
  return { midi, cents: (exact - midi) * CENTS_PER_SEMITONE }
}
